import { useCallback, useLayoutEffect, useState } from 'react';
import { Optional } from 'types/util/Optional';

export default function useScrollMargin(element: Optional<HTMLElement>, scroller: Optional<HTMLElement>) {
  const [scrollMargin, setScrollMargin] = useState(0);

  const measure = useCallback(() => {
    if (!element || !scroller || scroller.clientHeight === 0) {
      return;
    }

    const margin = element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;

    setScrollMargin((current) => (Math.abs(current - margin) < 1 ? current : margin));
  }, [element, scroller]);

  useLayoutEffect(() => {
    measure();

    if (!element || !scroller) {
      return;
    }

    const resizeObserver = new ResizeObserver(measure);
    const observeContentAbove = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(scroller);

      const children = Array.from(scroller.children);
      const containingIndex = children.findIndex((child) => child.contains(element));

      children.slice(0, containingIndex === -1 ? 0 : containingIndex).forEach((child) => {
        resizeObserver.observe(child);
      });
    };

    observeContentAbove();

    const mutationObserver = new MutationObserver(() => {
      observeContentAbove();
      measure();
    });
    mutationObserver.observe(scroller, { childList: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, scroller, measure]);

  return element && scroller ? scrollMargin : 0;
}
